import { COLOR_STYLE } from 'app/shared/u21-ui/components/display/typography/styles';

import { forwardRef, HTMLProps, ReactElement, ReactNode } from 'react';
import { getDOMProps, isRenderable } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';
import { Typography, TypographyProps } from '@mui/material';
import {
  U21Spacer,
  U21SpacerProps,
} from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21TypographyIcon } from 'app/shared/u21-ui/components/display/typography/U21TypographyIcon';
import {
  U21Tooltip,
  U21TooltipProps,
} from 'app/shared/u21-ui/components/display/U21Tooltip';
import { U21TypographyColor } from 'app/shared/u21-ui/components/display/typography/models';
import { U21Asterisk } from 'app/shared/u21-ui/components/display/U21Asterisk';
import DOMPurify from 'dompurify';

export interface U21TypographyProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'ref'
> {
  align?: U21SpacerProps['align'];
  breakWord?: boolean;
  children?: ReactNode;
  color?: U21TypographyColor;
  component?: TypographyProps['component'];
  ellipsis?: boolean;
  htmlString?: string;
  icon?: ReactElement;
  iconColor?: U21TypographyColor;
  inline?: boolean;
  maxLines?: number;
  required?: boolean;
  spacingProps?: Partial<Omit<U21SpacerProps, 'children'>>;
  textAlign?: TypographyProps['align'];
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  tooltip?: ReactNode;
  tooltipProps?: Omit<U21TooltipProps, 'children' | 'tooltip'>;
  variant?: TypographyProps['variant'];
}

export const U21Typography = forwardRef<HTMLDivElement, U21TypographyProps>(
  (props, ref) => {
    const {
      align,
      breakWord,
      children,
      color = 'text.primary',
      component,
      ellipsis,
      htmlFor,
      htmlString,
      icon,
      iconColor,
      inline,
      maxLines,
      required,
      spacingProps,
      textAlign,
      textTransform,
      tooltip,
      tooltipProps,
      variant = 'body1',
      ...rest
    } = props;

    // Show full text in tooltip if ellipsis / maxLines cut off text
    const { ref: internalRef } = useResizeDetector<HTMLSpanElement>({
      refreshMode: 'debounce',
      refreshRate: 300,
    });

    const hasFullTextTooltip =
      maxLines !== undefined
        ? (internalRef?.current?.scrollHeight || 0) >
          (internalRef?.current?.clientHeight || 0)
        : (internalRef?.current?.scrollWidth || 0) >
          (internalRef?.current?.clientWidth || 0);

    if (!isRenderable(children) && !htmlString) {
      return null;
    }

    const displayTooltip = (() => {
      if (tooltip && hasFullTextTooltip) {
        return (
          <U21Spacer dividers spacing={0.5} useFlexGap>
            {children}
            {tooltip}
          </U21Spacer>
        );
      }
      if (tooltip) {
        return tooltip;
      }
      if (hasFullTextTooltip) {
        return children;
      }
      return '';
    })();

    return (
      <Container
        $inline={inline}
        $ellipsis={ellipsis}
        align={align}
        horizontal
        ref={ref}
        useFlexGap
        {...spacingProps}
        {...getDOMProps(rest)}
      >
        <StyledU21TypographyIcon
          $align={align}
          color={iconColor ?? color}
          variant={variant}
        >
          {icon}
        </StyledU21TypographyIcon>
        <U21Tooltip tooltip={displayTooltip} {...tooltipProps}>
          {htmlString ? (
            <StyledTypography
              $color={color}
              $ellipsis={ellipsis}
              $breakWord={breakWord}
              $maxLines={maxLines}
              component={htmlFor ? 'label' : component}
              // @ts-expect-error prop is valid because we force component to label if it exists
              htmlFor={htmlFor}
              variant={variant}
              align={textAlign}
              ref={internalRef}
              textTransform={textTransform}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(htmlString),
              }}
            />
          ) : (
            <StyledTypography
              $color={color}
              $ellipsis={ellipsis}
              $breakWord={breakWord}
              $maxLines={maxLines}
              component={htmlFor ? 'label' : component}
              // @ts-expect-error prop is valid because we force component to label if it exists
              htmlFor={htmlFor}
              variant={variant}
              align={textAlign}
              ref={internalRef}
              textTransform={textTransform}
            >
              {children}
              {/* defined asterisk here if not ellipsis so it always appears after the text */}
              {!ellipsis && required && <U21Asterisk />}
            </StyledTypography>
          )}
        </U21Tooltip>
        {/* defined asterisk here if ellipsis so it is not ellipsed */}
        {ellipsis && required && (
          <AsteriskTypography variant={variant}>
            <U21Asterisk />
          </AsteriskTypography>
        )}
      </Container>
    );
  },
);

const Container = styled(U21Spacer)<{ $inline?: boolean; $ellipsis?: boolean }>`
  ${(props) =>
    props.$inline &&
    css`
      display: inline-flex;
    `}
  ${(props) =>
    props.$ellipsis &&
    css`
      overflow: hidden;
    `}
`;

const StyledU21TypographyIcon = styled(U21TypographyIcon)<{
  $align: U21SpacerProps['align'];
}>`
  ${(props) => {
    const {
      $align,
      theme: { typography },
      variant,
    } = props;
    const { remToPx } = typography;
    const properties = typography[variant!]; // variant is guaranteed to be defined here
    const { fontSize, lineHeight } = properties;
    const base = remToPx(fontSize);
    const small = remToPx(
      properties['@media (min-width:600px)']?.fontSize || fontSize,
    );
    const medium = remToPx(
      properties['@media (min-width:960px)']?.fontSize || fontSize,
    );
    const large = remToPx(
      properties['@media (min-width:1280px)']?.fontSize || fontSize,
    );
    switch ($align) {
      case 'start':
        return css`
          &&& {
            margin-top: ${(base * lineHeight - base) / 2}px;
            @media (min-width: 600px) {
              margin-top: ${(small * lineHeight - small) / 2}px;
            }
            @media (min-width: 960px) {
              margin-top: ${(medium * lineHeight - medium) / 2}px;
            }
            @media (min-width: 1280px) {
              margin-top: ${(large * lineHeight - large) / 2}px;
            }
          }
        `;
      case 'end':
        return css`
          &&& {
            margin-bottom: ${(base * lineHeight - base) / 2}px;
            @media (min-width: 600px) {
              margin-bottom: ${(small * lineHeight - small) / 2}px;
            }
            @media (min-width: 960px) {
              margin-bottom: ${(medium * lineHeight - medium) / 2}px;
            }
            @media (min-width: 1280px) {
              margin-bottom: ${(large * lineHeight - large) / 2}px;
            }
          }
        `;
      default:
        return css``;
    }
  }}
`;

interface StyledTypographyProps {
  $color: string;
  $ellipsis?: boolean;
  $breakWord?: boolean;
  $maxLines?: number;
  component?: TypographyProps['component'];
}

const StyledTypography = styled(Typography)<StyledTypographyProps>`
  ${COLOR_STYLE}

  ${(props) =>
    props.$breakWord &&
    css`
      word-break: break-word;
    `}

  ${(props) => {
    if (props.$maxLines !== undefined) {
      return css`
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${props.$maxLines};
      `;
    } else if (props.$ellipsis) {
      return css`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;
    }
    return css``;
  }}
`;

const AsteriskTypography = styled(Typography)`
  flex-shrink: 0;
  // override U21Spacer gap
  margin-left: -8px;
`;
